import React from 'react';
import clsx from 'clsx';

export type IconButtonIntent = 'secondary' | 'ghost' | 'danger';
export type IconButtonSize = 'sm' | 'md' | 'lg';

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Accessible label read by screen readers. Required — icons alone aren't readable. */
  'aria-label': string;
  intent?: IconButtonIntent;
  size?: IconButtonSize;
}

const intentClasses: Record<IconButtonIntent, string> = {
  secondary:
    'bg-white/5 text-slate-300 border-white/[0.08] hover:bg-white/10 hover:text-slate-50',
  ghost:
    'bg-transparent text-slate-300 border-transparent hover:bg-white/5 hover:text-slate-50',
  danger:
    'bg-transparent text-danger-text border-transparent hover:bg-danger-tint',
};

const sizeClasses: Record<IconButtonSize, string> = {
  sm: 'w-7 h-7',
  md: 'w-8 h-8',
  lg: 'w-9 h-9',
};

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ intent = 'ghost', size = 'md', className, type, ...props }, ref) => (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={clsx(
        'inline-flex items-center justify-center rounded-ds-lg border transition-colors duration-150',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface-1',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        intentClasses[intent],
        sizeClasses[size],
        className,
      )}
      {...props}
    />
  ),
);

IconButton.displayName = 'IconButton';

export default IconButton;
